import { useState, useEffect, useRef } from "react";

export interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
}

export interface VideoMetadataError {
  error: string;
}

function getVideoMetadataFromUrl(url: string, { crossOrigin = false } = {}): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    if (crossOrigin) video.crossOrigin = 'anonymous';

    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
      // remove source and free resources
      try { video.src = ''; video.load(); } catch (e) {}
    };

    const onLoaded = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      });
      cleanup();
    };

    const onError = (e) => {
      reject(new Error('Error loading video metadata'));
      cleanup();
    };

    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('error', onError);

    video.src = url;
    // ensure loading is triggered
    video.load();
  });
}

export function useVideoMetadata(source: File | string | null, { crossOrigin = false } = {}): VideoMetadata | VideoMetadataError | null {
  const [meta, setMeta] = useState<VideoMetadata | VideoMetadataError | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!source) {
      setMeta(null);
      return;
    }

    // allow passing either a File object or a string URL
    let url: string;
    if (source instanceof File) {
      url = URL.createObjectURL(source);
      urlRef.current = url;
    } else {
      url = source;
    }

    let mounted = true;

    getVideoMetadataFromUrl(url, { crossOrigin })
      .then((m: VideoMetadata) => { if (mounted) setMeta(m); })
      .catch((err: Error) => { if (mounted) setMeta({ error: err.message }); });

    return () => {
      mounted = false;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [source, crossOrigin]);

  return meta;
}

export default useVideoMetadata;